import React from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import {
  Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from 'lucide-react';
import { PriceList, PriceListCreate, PriceListUpdate, ApplicableToEnum, DiscountTypeEnum } from '@/app/types/pricing';

// Zod Schema for Price List Form
const formSchema = z.object({
  name: z.string().min(1, 'List name is required').max(100),
  description: z.string().optional().nullable(),
  applicable_to: z.enum(['customers', 'vendors', 'all']),
  default_discount_type: z.enum(['percentage', 'fixed']).nullable().optional(),
  default_discount_value: z.string().refine(val => val === '' || (!isNaN(parseFloat(val)) && parseFloat(val) >= 0), {
    message: "Must be a non-negative number",
  }).optional().nullable(), // String for input
  effective_from: z.string().optional().nullable(),
  effective_to: z.string().optional().nullable(),
  is_active: z.boolean().default(true),
}).refine(data => { // Validation for discount value based on type
    if (data.default_discount_type && (data.default_discount_value === null || data.default_discount_value === undefined || data.default_discount_value === '')) {
      return false; // Value required if type is selected
    }
    if (!data.default_discount_type && data.default_discount_value && parseFloat(data.default_discount_value) !== 0) {
        return false; // Value must be 0 or empty if type is not selected
    }
    return true;
  }, {
    message: "Default discount value is required when type is selected, and must be 0 if type is not selected.",
    path: ["default_discount_value"], // Attach error to the value field
  });

type PriceListFormValues = z.infer<typeof formSchema>;

interface PriceListFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: PriceListCreate | PriceListUpdate) => void;
  defaultValues?: Partial<PriceList>;
  isLoading?: boolean;
  mode: 'create' | 'edit';
}

const PriceListForm: React.FC<PriceListFormProps> = ({
  open,
  onOpenChange,
  onSubmit,
  defaultValues,
  isLoading = false,
  mode,
}) => {
  const form = useForm<PriceListFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: defaultValues?.name || '',
      description: defaultValues?.description || '',
      applicable_to: defaultValues?.applicable_to || 'customers',
      default_discount_type: defaultValues?.default_discount_type || null,
      default_discount_value: String(defaultValues?.default_discount_value ?? ''),
      effective_from: defaultValues?.effective_from || '',
      effective_to: defaultValues?.effective_to || '',
      is_active: defaultValues?.is_active ?? true,
    },
  });

  const handleSubmit: SubmitHandler<PriceListFormValues> = (values) => {
    // Convert discount value back to number before submitting
    const payload = {
      ...values,
      default_discount_value: values.default_discount_value ? parseFloat(values.default_discount_value) : 0,
      // Ensure optional date fields are null if empty
      effective_from: values.effective_from || null,
      effective_to: values.effective_to || null,
    };
    onSubmit(payload);
  };

  const discountType = form.watch('default_discount_type');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Create New Price List' : 'Edit Price List'}</DialogTitle>
          <DialogDescription>
            {mode === 'create' ? 'Define a new price list.' : 'Update the price list details.'}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 py-2">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name*</FormLabel>
                  <FormControl><Input {...field} placeholder="e.g., Retail, Wholesale" autoFocus /></FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl><Textarea {...field} value={field.value ?? ''} placeholder="Optional description" rows={2} /></FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

             <div className="grid grid-cols-2 gap-4">
                 <FormField
                  control={form.control}
                  name="applicable_to"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Applicable To*</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl><SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="customers">Customers</SelectItem>
                          <SelectItem value="vendors">Vendors</SelectItem>
                          <SelectItem value="all">All</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                 <FormField
                  control={form.control}
                  name="default_discount_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Default Discount Type</FormLabel>
                       <Select
                        onValueChange={(value) => field.onChange(value === 'none' ? null : value)}
                        value={field.value ?? 'none'}
                        >
                        <FormControl><SelectTrigger><SelectValue placeholder="Select type (Optional)" /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="percentage">Percentage (%)</SelectItem>
                          <SelectItem value="fixed">Fixed Amount</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
             </div>

            {discountType && (
               <FormField
                  control={form.control}
                  name="default_discount_value"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Default Discount Value</FormLabel>
                      <FormControl>
                         <Input
                            {...field}
                            type="number"
                            step="0.01"
                            placeholder="Enter value"
                            value={field.value ?? ''}
                            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                         />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
            )}

             <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="effective_from"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Effective From</FormLabel>
                      <FormControl><Input {...field} value={field.value ?? ''} type="date" /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                 <FormField
                  control={form.control}
                  name="effective_to"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Effective To</FormLabel>
                      <FormControl><Input {...field} value={field.value ?? ''} type="date" /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
            </div>

             <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-3 space-y-0 pt-2">
                  <FormControl><Checkbox checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Active</FormLabel>
                    <FormDescription>Inactive lists cannot be used.</FormDescription>
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => onOpenChange(false)} disabled={isLoading}>Cancel</Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {isLoading ? (mode === 'create' ? 'Creating...' : 'Updating...') : (mode === 'create' ? 'Create List' : 'Save Changes')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default PriceListForm; 